import React, {useEffect, useState, forwardRef, useImperativeHandle, useRef} from 'react';
import {useIntl} from "react-intl";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {Col, Form, message, Row, Badge} from "antd";
import {CompanyMultiSelector, CompanySelectorOption} from "./CompanyMultiSelector";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {fetchPlaneCategories} from "../../../../../utils/helpers/selectDataFetchers/fetchPlaneCategories";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {NewTripRequestPageMessages} from "../NewTripRequestPageMessages";
import styled from "styled-components";
import {Dayjs} from "dayjs";
import {RecursivePartial} from "../../../../../utils/helpers/tsHelpers/reqursivePartialType";
import {TripItem} from "./TripItem";
import {PlusOutlined} from "@ant-design/icons";
import {
  brokerAircraftSearch,
  clearBrokerAircraftSearch,
} from "../../../../../store/reducers/newRequestReducer/newRequestReducer";
import {useIsMobile} from "../../../../../hooks/useWindowSize";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {defineMessages} from "react-intl";
import {AdvancedSearchModal} from "./AdvancedSearchModal";

const FormContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const FieldsRow = styled(Row)`
  width: 100%;
`;

const ButtonsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: ${props => props.isMobile ? '10px' : '24px'};
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
  flex-direction: ${props => props.isMobile ? 'column' : 'row'};
  
  button {
    ${props => props.isMobile ? 'width: 100%;' : ''}
  }
  
  .ant-badge {
    ${props => props.isMobile ? 'width: 100%;' : ''}
    
    .ant-badge-count {
      min-width: 20px !important;
      height: 20px !important;
      border-radius: 10px !important;
      padding: 0 6px !important;
      line-height: 20px !important;
      font-size: 12px !important;
      box-sizing: border-box !important;
      ${props => props.isMobile ? 'right: 7px !important;' : ''}
    }
  }
`;

const MultiLegFormMessages = defineMessages({
  showAdvancedSearch: {
    id: "newTripRequestPage.showAdvancedSearch",
    defaultMessage: "Показать расширенный поиск",
  },
});

type MultiLegRouteType = {
  airportFromId: number;
  airportToId: number;
  departureDate: Dayjs;
  pax: number;
}

interface IMultiLegFormData {
  routes: MultiLegRouteType[];
  planeCategoryIds: number[],
  companionsAllowed: boolean;
  comment: string;
  companyIds: number[];
}

interface IMultiLegFormProps {
  clearTrigger?: number;
}

export const MultiLegForm = forwardRef<{ resetForm: () => void }, IMultiLegFormProps>(({ clearTrigger }, ref) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [companyList, setCompanyList] = useState<CompanySelectorOption[]>([]);
  const [isAdvancedSearchModalOpen, setIsAdvancedSearchModalOpen] = useState<boolean>(false);
  const {
    isLoading: isSearchLoading,
  } = useAppSelector(state => state.newRequest.brokerAircraftSearch);
  
  // Ref to track previous routes to detect changes
  const prevRoutesRef = useRef<MultiLegRouteType[]>([]);

  useEffect(() => {
    return () => {
      dispatch(clearBrokerAircraftSearch())
    }
  }, [])

  // Сброс формы при изменении clearTrigger
  useEffect(() => {
    if (clearTrigger && clearTrigger > 0) {
      form.resetFields();
      setCompanyList([]);
      prevRoutesRef.current = [];
    }
  }, [clearTrigger, form]);

  // Watch routes for changes and auto-populate next leg's departure airport
  const routes = Form.useWatch('routes', form) || [];
  
  useEffect(() => {
    if (!routes || routes.length === 0) {
      prevRoutesRef.current = [];
      return;
    }

    const prevRoutes = prevRoutesRef.current;

    // Check each route to see if destination airport changed
    routes.forEach((route: MultiLegRouteType, index: number) => {
      const prevRoute = prevRoutes[index];
      
      // If destination airport changed and there's a next leg without departure airport
      if (route?.airportToId && route.airportToId !== prevRoute?.airportToId) {
        const nextLegIndex = index + 1;
        const nextRoute = routes[nextLegIndex];
        
        if (nextRoute && !nextRoute.airportFromId) {
          const updatedRoutes = [...routes];
          updatedRoutes[nextLegIndex] = {
            ...nextRoute,
            airportFromId: route.airportToId,
          };
          form.setFieldsValue({ routes: updatedRoutes });
        }
      }
    });

    // Update ref with current routes
    prevRoutesRef.current = routes.map((route: MultiLegRouteType) => ({
      airportFromId: route?.airportFromId,
      airportToId: route?.airportToId,
      departureDate: route?.departureDate,
      pax: route?.pax,
    }));
  }, [routes, form]);

  // Экспортируем метод resetForm через ref
  useImperativeHandle(ref, () => ({
    resetForm: () => {
      form.resetFields();
      setCompanyList([]);
    }
  }));

  const onFinish = (values: IMultiLegFormData) => {
    // Для MultiLeg используем первый маршрут для поиска самолетов
    if (values.routes.length === 0) {
      message.error(intl.formatMessage(CommonFormMessages.ruleRequired));
      return;
    }

    const firstRoute = values.routes[0];
    // Всегда передаем время, так как оно нужно для определения местоположения самолета по Schedule
    const departureDate = firstRoute.departureDate.format('YYYY-MM-DD');
    const departureTime = firstRoute.departureDate.isSame(firstRoute.departureDate.startOf('day'))
      ? '00:00:00'
      : firstRoute.departureDate.format('HH:mm');

    // Получаем данные из расширенного поиска
    const advancedSearchValues = form.getFieldsValue(['planeModelIds', 'productionYearRange', 'overhaulYearRange', 
      'flightDurationRange', 'landingsCount', 'refuelsCount', 'planeUpdateDate', 'amenitiesIds', 'operatorIds', 'safetyStandardsIds']);

    // Преобразуем данные из расширенного поиска
    const planeModel = advancedSearchValues.planeModelIds?.length > 0 
      ? advancedSearchValues.planeModelIds.join(', ') 
      : undefined;
    
    const manufactureYearFrom = advancedSearchValues.productionYearRange?.[0];
    const manufactureYearTo = advancedSearchValues.productionYearRange?.[1];
    
    const renovationYearFrom = advancedSearchValues.overhaulYearRange?.[0];
    const renovationYearTo = advancedSearchValues.overhaulYearRange?.[1];
    
    const positioningDurationFromHours = advancedSearchValues.flightDurationRange?.[0];
    const positioningDurationToHours = advancedSearchValues.flightDurationRange?.[1];
    
    // Обрабатываем landingCount и refuelingCount
    let landingCountFrom: number | undefined;
    let landingCountTo: number | undefined;
    if (advancedSearchValues.landingsCount && Array.isArray(advancedSearchValues.landingsCount)) {
      const landingValues = advancedSearchValues.landingsCount.map((v: string) => {
        if (v === 'no-landings') return 0;
        if (v === 'one-landing') return 1;
        if (v === 'two-plus-landings') return 2;
        return undefined;
      }).filter((v: number | undefined) => v !== undefined) as number[];
      if (landingValues.length > 0) {
        landingCountFrom = Math.min(...landingValues);
        landingCountTo = Math.max(...landingValues) === 2 ? undefined : Math.max(...landingValues);
      }
    }
    
    let refuelingCountFrom: number | undefined;
    let refuelingCountTo: number | undefined;
    if (advancedSearchValues.refuelsCount && Array.isArray(advancedSearchValues.refuelsCount)) {
      const refuelValues = advancedSearchValues.refuelsCount.map((v: string) => {
        if (v === 'no-refuels') return 0;
        if (v === 'one-refuel') return 1;
        if (v === 'two-refuels') return 2;
        if (v === 'three-plus-refuels') return 3;
        return undefined;
      }).filter((v: number | undefined) => v !== undefined) as number[];
      if (refuelValues.length > 0) {
        refuelingCountFrom = Math.min(...refuelValues);
        refuelingCountTo = Math.max(...refuelValues) === 3 ? undefined : Math.max(...refuelValues);
      }
    }
    
    // Преобразуем updatedAtFrom из даты
    const updatedAtFrom = advancedSearchValues.planeUpdateDate 
      ? advancedSearchValues.planeUpdateDate.format('YYYY-MM-DD')
      : undefined;
    
    // Преобразуем amenities из расширенного поиска
    const amenities: string[] = [];
    if (advancedSearchValues.amenitiesIds && Array.isArray(advancedSearchValues.amenitiesIds)) {
      amenities.push(...advancedSearchValues.amenitiesIds.filter((id: string) => 
        ['air_ambulance', 'animals_aboard', 'smoking_aboard', 'air_cargo_freight', 'wifi', 'oven_aboard'].includes(id)
      ));
    }

    // Преобразуем operatorIds из модалки расширенного поиска
    const operatorIds = advancedSearchValues.operatorIds && Array.isArray(advancedSearchValues.operatorIds) && advancedSearchValues.operatorIds.length > 0
      ? advancedSearchValues.operatorIds
      : undefined;

    // Используем максимальное количество пассажиров из всех маршрутов
    const maxPax = Math.max(...values.routes.map(r => r.pax));
    
    // Prepare multileg routes for the search request
    const multilegRoutes = values.routes.map(route => ({
      airportFromId: route.airportFromId,
      airportToId: route.airportToId,
      departureDate: route.departureDate.format('YYYY-MM-DD'),
      departureTime: route.departureDate.isSame(route.departureDate.startOf('day'))
        ? '00:00:00'
        : route.departureDate.format('HH:mm'),
      pax: route.pax,
    }));

    dispatch(brokerAircraftSearch({
      originAirportId: firstRoute.airportFromId,
      destinationAirportId: firstRoute.airportToId,
      departureDate,
      departureTime,
      pax: maxPax,
      multilegRoutes, // Send all multileg routes
      planeCategoryIds: values.planeCategoryIds || [],
      planeModel,
      manufactureYearFrom,
      manufactureYearTo,
      renovationYearFrom,
      renovationYearTo,
      positioningDurationFromHours,
      positioningDurationToHours,
      landingCountFrom,
      landingCountTo,
      refuelingCountFrom,
      refuelingCountTo,
      updatedAtFrom,
      operatorIds,
      amenities: amenities.length > 0 ? amenities : undefined,
      page: 0,
      limit: 10,
    }));
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const initialValues: RecursivePartial<IMultiLegFormData> = {
    routes: [
      {
        airportFromId: undefined,
        airportToId: undefined,
        departureDate: undefined,
        pax: undefined,
      },
      {
        airportFromId: undefined,
        airportToId: undefined,
        departureDate: undefined,
        pax: undefined,
      }
    ],
    planeCategoryIds: [],
    companionsAllowed: false,
    comment: undefined,
    companyIds: [],
  }

  const companyMultiSelector = <CompanyMultiSelector selectedCompanies={companyList} setSelectedCompanies={setCompanyList}/>
  
  // Подсчитываем количество примененных фильтров advanced search
  const advancedSearchFieldsCount = (() => {
    let count = 0;
    
    // Компании (отдельный компонент, не из модалки)
    if (companyList.length > 0) count++;
    
    // Получаем все значения из формы
    const formValues = form.getFieldsValue([
      'planeModelIds', 
      'productionYearRange', 
      'overhaulYearRange', 
      'flightDurationRange', 
      'landingsCount', 
      'refuelsCount', 
      'planeUpdateDate', 
      'amenitiesIds', 
      'operatorIds', 
      'safetyStandardsIds',
      'companionsAllowed',
      'comment'
    ]);
    
    // Модель самолета
    if (formValues.planeModelIds && Array.isArray(formValues.planeModelIds) && formValues.planeModelIds.length > 0) count++;
    
    // Ползунки - учитываем только если они установлены в форме (модалка была открыта и сохранена)
    if (formValues.productionYearRange && Array.isArray(formValues.productionYearRange) && formValues.productionYearRange.length === 2) count++;
    if (formValues.overhaulYearRange && Array.isArray(formValues.overhaulYearRange) && formValues.overhaulYearRange.length === 2) count++;
    if (formValues.flightDurationRange && Array.isArray(formValues.flightDurationRange) && formValues.flightDurationRange.length === 2) count++;
    
    // Количество посадок
    if (formValues.landingsCount && Array.isArray(formValues.landingsCount) && formValues.landingsCount.length > 0) count++;
    
    // Количество дозаправок
    if (formValues.refuelsCount && Array.isArray(formValues.refuelsCount) && formValues.refuelsCount.length > 0) count++;
    
    // Дата обновления самолета
    if (formValues.planeUpdateDate) count++;
    
    // Удобства на борту
    if (formValues.amenitiesIds && Array.isArray(formValues.amenitiesIds) && formValues.amenitiesIds.length > 0) count++;
    
    // Операторы
    if (formValues.operatorIds && Array.isArray(formValues.operatorIds) && formValues.operatorIds.length > 0) count++;
    
    // Стандарты безопасности
    if (formValues.safetyStandardsIds && Array.isArray(formValues.safetyStandardsIds) && formValues.safetyStandardsIds.length > 0) count++;
    
    // Разрешены спутники
    if (formValues.companionsAllowed) count++;
    
    // Комментарий
    if (formValues.comment && formValues.comment.trim().length > 0) count++;
    
    return count;
  })();
  
  const handleAdvancedSearchSave = (values: any) => {
    form.setFieldsValue({
      comment: values.comment,
      companionsAllowed: values.companionsAllowed,
      planeModelIds: values.planeModelIds,
      productionYearRange: values.productionYearRange,
      overhaulYearRange: values.overhaulYearRange,
      flightDurationRange: values.flightDurationRange,
      landingsCount: values.landingsCount,
      refuelsCount: values.refuelsCount,
      planeUpdateDate: values.planeUpdateDate,
      amenitiesIds: values.amenitiesIds,
      operatorIds: values.operatorIds,
      safetyStandardsIds: values.safetyStandardsIds,
    });
  };

  return (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      autoComplete="off"
      layout={'vertical'}
      form={form}
    >
      <FormContentWrapper>
        <Form.List name="routes">
          {(fields, {add, remove}) => {
            const handleAddLeg = () => {
              const currentRoutes = form.getFieldValue('routes') || [];
              const previousLeg = currentRoutes[currentRoutes.length - 1];
              
              // Auto-populate departure from previous leg's destination if available
              add({
                airportFromId: previousLeg?.airportToId,
                airportToId: undefined,
                departureDate: undefined,
                pax: undefined,
              });
            };
            
            return (
              <>
                {fields.map((field, index) =>
                  <TripItem key={field.key} field={field} onRemoveClick={() => remove(field.name)}
                            routeNumber={index + 1} form={form}/>
                )}
                {fields.length < 10 && <CustomFieldInput>
                  <CustomButton type={"primary"} ghost onClick={handleAddLeg} block icon={<PlusOutlined/>} style={{marginBottom: 8}}>
                    {intl.formatMessage(NewTripRequestPageMessages.addFlight)}
                  </CustomButton>
                </CustomFieldInput>}
              </>
            );
          }}
        </Form.List>

        <FieldsRow gutter={8}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.planeCategoryLabel)}
              name={'planeCategoryIds'}
            >
              <DebounceSelect
                fetchOptions={fetchPlaneCategories}
                placeholder={intl.formatMessage(CommonFormMessages.allPlaceholder)}
                mode={'multiple'}
                brightPlaceholder={true}
              />
            </CustomFieldInput>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <ButtonsWrapper isMobile={isMobile} style={{marginTop: '28px', justifyContent: isMobile ? 'stretch' : 'flex-end'}}>
              <div style={{width: isMobile ? '100%' : 'auto'}}>
                <Badge count={advancedSearchFieldsCount} offset={[-4, 4]} style={{display: isMobile ? 'block' : 'inline-block'}}>
                  <CustomButton
                    onClick={() => setIsAdvancedSearchModalOpen(true)}
                    style={{
                      border: '1px solid #448ef8',
                      color: '#000',
                      background: '#fff',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    + {intl.formatMessage(MultiLegFormMessages.showAdvancedSearch)}
                  </CustomButton>
                </Badge>
              </div>
              <CustomButton
                type="primary"
                htmlType="submit"
                loading={isSearchLoading}
                style={{minWidth: isMobile ? '100%' : '119px', width: isMobile ? '100%' : 'auto'}}
              >
                {intl.formatMessage(CommonButtonMessages.find)}
              </CustomButton>
            </ButtonsWrapper>
          </Col>
        </FieldsRow>

      </FormContentWrapper>

      <AdvancedSearchModal
        isModalVisible={isAdvancedSearchModalOpen}
        setIsModalVisible={setIsAdvancedSearchModalOpen}
        onSave={handleAdvancedSearchSave}
        initialValues={{
          comment: form.getFieldValue('comment'),
          companionsAllowed: form.getFieldValue('companionsAllowed'),
          planeModelIds: form.getFieldValue('planeModelIds'),
          productionYearRange: form.getFieldValue('productionYearRange'),
          overhaulYearRange: form.getFieldValue('overhaulYearRange'),
          flightDurationRange: form.getFieldValue('flightDurationRange'),
          landingsCount: form.getFieldValue('landingsCount'),
          refuelsCount: form.getFieldValue('refuelsCount'),
          planeUpdateDate: form.getFieldValue('planeUpdateDate'),
          amenitiesIds: form.getFieldValue('amenitiesIds'),
          operatorIds: form.getFieldValue('operatorIds'),
          safetyStandardsIds: form.getFieldValue('safetyStandardsIds'),
        }}
      />
    </Form>
  );
});
